import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, X, MessageSquare, Trash2, Plus, History } from 'lucide-react';
import { useAgent } from '../../hooks/useAgent';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { AgentTypingIndicator } from './AgentTypingIndicator';
import { AgentWelcome } from './AgentWelcome';

interface ChecklistAgentProps {
  user: {
    id: string;
    username: string;
    role: 'bank' | 'company';
  };
  currentBank?: {
    id: string;
    name: string;
  };
}

export const ChecklistAgent: React.FC<ChecklistAgentProps> = ({ user, currentBank }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showHistory, setShowHistory] = useState(true);

  const {
    messages,
    conversations,
    currentConversation,
    isLoading,
    error,
    sendMessage,
    loadConversations,
    loadConversation,
    deleteConversation,
    startNewConversation,
    clearError
  } = useAgent(user.id, currentBank?.id);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSendMessage = async (message: string) => {
    await sendMessage(message);
  };

  const showWelcome = messages.length === 0 && !isLoading;

  const handleDeleteConversation = async (conversationId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questa conversazione?')) {
      await deleteConversation(conversationId);
    }
  };

  return (
    <div className="flex h-full bg-gray-50">
      {/* History Sidebar */}
      {showHistory && (
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          {/* Sidebar Header */}
          <div className="px-4 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-600" />
                <h2 className="font-semibold text-gray-900">Storico Conversazioni</h2>
              </div>
              <button
                onClick={() => setShowHistory(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <button
              onClick={startNewConversation}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nuova Conversazione
            </button>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                Nessuna conversazione salvata
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      currentConversation?.id === conv.id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''
                    }`}
                    onClick={() => loadConversation(conv.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate text-sm">
                          {conv.title || 'Conversazione senza titolo'}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(conv.updatedAt).toLocaleString('it-IT', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteConversation(conv.id);
                        }}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!showHistory && (
                <button
                  onClick={() => setShowHistory(true)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <History className="w-6 h-6" />
                </button>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Assistente Checklist</h1>
                <p className="text-sm text-gray-600 mt-1">
                  {user.role === 'bank' && currentBank
                    ? `Banca: ${currentBank.name}`
                    : 'Tutte le banche'}
                  {currentConversation && ` • ${currentConversation.title || 'Nuova conversazione'}`}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
            <button
              onClick={clearError}
              className="text-red-600 hover:text-red-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {showWelcome ? (
            <AgentWelcome onSuggestedPrompt={handleSendMessage} />
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}
              {isLoading && <AgentTypingIndicator />}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Area */}
        <ChatInput
          onSend={handleSendMessage}
          disabled={isLoading}
          placeholder={
            isLoading
              ? 'Attendere...'
              : 'Chiedi qualcosa sulle checklist o sulla normativa...'
          }
        />
      </div>
    </div>
  );
};
